import { MoreOutlined } from "@ant-design/icons";
import { influencerActionsEnum } from "@entities/brief/lib/constants";
import { useDeclineInfluencer } from "@entities/brief/lib/hooks/useDeclineInfluencer";
import { PRICING_POC_FACETS_QUERY_KEY } from "@entities/pricingWorkload/lib/constants";
import { InfluencerCommercial } from "@features/InfluencerCommercialsV2/InfluencerCommercialsCost/InfluencerCommercial";
import { ToastService } from "@shared/lib/services";
import { IBriefInfluencerV2 } from "@shared/lib/types/briefInfluencerV2";
import { Button, Dropdown, Input, Modal, Popover } from "@shared/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import styles from "./styles.module.scss";

interface CommercialsActionsMenuProps {
  briefId: string;
  briefInfluencer: IBriefInfluencerV2;
  pocId: string;
  disabled?: boolean;
  compact?: boolean;
  /** Show "Add New" only when cost input is hidden (existing cost, non-negotiation). */
  showAddNew?: boolean;
}

export function CommercialsActionsMenu({
  briefId,
  briefInfluencer,
  pocId,
  disabled = false,
  compact = false,
  showAddNew = false,
}: CommercialsActionsMenuProps) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState("");
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const [isAddNewOpen, setIsAddNewOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const {
    decline: declineInfluencer,
    isDeclining: isDecliningInfluencer,
    isDeclined: isDeclinedInfluencer,
  } = useDeclineInfluencer(briefId);

  const handleDecline = async () => {
    await declineInfluencer({
      reason,
      briefInfluencerId: briefInfluencer._id,
    });
  };

  useEffect(() => {
    if (isDeclinedInfluencer) {
      ToastService.success("Influencer declined successfully.");
      setIsDeclineModalOpen(false);
      setReason("");
      queryClient.invalidateQueries({
        queryKey: PRICING_POC_FACETS_QUERY_KEY(briefId, pocId),
      });
    }
  }, [briefId, isDeclinedInfluencer, pocId, queryClient]);

  const menuItems = [
    {
      key: "creator-decline",
      label: "Creator Decline",
      onClick: () => {
        setIsDropdownOpen(false);
        setIsDeclineModalOpen(true);
      },
    },
    ...(showAddNew
      ? [
          {
            key: "add-new",
            label: influencerActionsEnum.addNewInfluencerCommercial,
            onClick: () => {
              setIsDropdownOpen(false);
              setIsAddNewOpen(true);
            },
          },
        ]
      : []),
  ];

  const dropdown = (
    <Dropdown
      trigger={["click"]}
      placement="bottomRight"
      open={isDropdownOpen}
      onOpenChange={setIsDropdownOpen}
      menu={{ items: menuItems }}
      disabled={disabled}
    >
      <Button
        type={compact ? "primary" : "text"}
        icon={<MoreOutlined />}
        size="small"
        className={compact ? styles.actionsMenuCompact : styles.actionsMenu}
        loading={isDecliningInfluencer}
        disabled={disabled}
        onClick={(event) => event.stopPropagation()}
      />
    </Dropdown>
  );

  return (
    <>
      {showAddNew ? (
        <Popover
          open={isAddNewOpen}
          trigger={["click"]}
          placement="right"
          title="Add Commercial"
          content={
            isAddNewOpen ? (
              <InfluencerCommercial
                briefInfluencer={briefInfluencer}
                type="create-new"
                onClose={() => setIsAddNewOpen(false)}
              />
            ) : null
          }
          onOpenChange={(open) => {
            if (!open) {
              setIsAddNewOpen(false);
            }
          }}
          destroyTooltipOnHide
        >
          {dropdown}
        </Popover>
      ) : (
        dropdown
      )}

      <Modal
        open={isDeclineModalOpen}
        title="Confirm"
        onCancel={() => setIsDeclineModalOpen(false)}
        onOk={handleDecline}
        okText="Continue"
        confirmLoading={isDecliningInfluencer}
        okButtonProps={{ disabled: !reason }}
      >
        <div className={styles.declineModalText}>
          Are you sure you want to drop the creator?
        </div>
        <div className={styles.declineModalLabel}>Enter the reason for Drop</div>
        <Input
          placeholder="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </Modal>
    </>
  );
}
